import SegmentLoadingButton from 'ui-component/Segment/SegmentLoadingButton';
import SegmentButton from 'ui-component/Segment/SegmentButton';
import SegmentIconButton from 'ui-component/Segment/SegmentIconButton';
import { useState } from 'react';
import {
  useDeleteShipmentLineMutation,
  useGetShipmentQuery,
} from 'store/slices/apiV1/inventory';
import { useParams } from 'react-router-dom';
import { handleErr } from 'utils/functions';
import { getCopy } from 'views/inventory/InventoryShipment/components/ShipmentRemoveStockLotDialog/copy';

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  GridLegacy,
  Typography,
} from '@mui/material';
import { StockLotCRUDSchema } from 'types/inventory';
import { IconX } from '@tabler/icons-react';
import { ALL_APPS } from 'constants/appConstants';
import { appPermissionAccessLevels } from 'types/apps';
import useSnackbar from 'hooks/useSnackbar';

const ShipmentRemoveStockLotDialog = ({
  lineId,
  dialogOpen,
  onClose,
}: {
  lineId: StockLotCRUDSchema['id'];
  dialogOpen: boolean;
  onClose: () => void;
}) => {
  const { shipmentId } = useParams<'shipmentId'>();
  const { data: shipment = null } = useGetShipmentQuery({
    id: shipmentId as string,
    expand: true,
  });

  const { dispatchSuccessSnackbar } = useSnackbar();
  const [error, setError] = useState('');
  const [deleteShipmentLine, { isLoading }] = useDeleteShipmentLineMutation();
  const copy = getCopy();

  return (
    <Dialog
      sx={{
        '& .MuiDialog-paper': { width: '80%', maxHeight: 435 },
      }}
      open={dialogOpen}
      onClose={onClose}
    >
      <DialogTitle>
        <GridLegacy container>
          <GridLegacy item xs>
            Remove Stock Lot
          </GridLegacy>
          <GridLegacy item xs="auto">
            <SegmentIconButton
              onClick={onClose}
              trackingName={copy.closeRemoveStockLotDialog}
            >
              <IconX />
            </SegmentIconButton>
          </GridLegacy>
        </GridLegacy>
      </DialogTitle>
      <DialogContent>
        <Typography>
          Are you sure you want to remove this stock lot from this shipment? You
          will not be able to undo this action.
        </Typography>
      </DialogContent>
      <DialogActions>
        {error && error.length > 0 && <Typography>{error}</Typography>}
        <SegmentButton
          size="medium"
          color="primary"
          onClick={() => {
            onClose();
          }}
          trackingName={copy.closeRemoveStockLotDialog}
        >
          Cancel
        </SegmentButton>
        <SegmentLoadingButton
          variant="contained"
          size="medium"
          color="error"
          onClick={async () => {
            try {
              await deleteShipmentLine({
                shipmentId: shipmentId as string,
                shipmentLineId: lineId,
                shipment: shipment ?? undefined,
              }).unwrap();
              dispatchSuccessSnackbar('Stock Lot removed');
              onClose();
            } catch (err) {
              handleErr(err, setError);
            }
          }}
          loading={isLoading}
          trackingName={copy.removeStockLot}
          permissionScope={{
            app: ALL_APPS.KITTING.id,
            accessLevel: appPermissionAccessLevels.edit,
          }}
        >
          Remove
        </SegmentLoadingButton>
      </DialogActions>
    </Dialog>
  );
};
export default ShipmentRemoveStockLotDialog;
